import { A2eModel, Providers } from '@d-id/types';
import { JsonError } from '../errors/did-json-error';
import { FaceRect, FaceSquare } from './IFaceSquare';
import { ImageType, VideoType } from './IVideo';
import { AvatarStatus } from './Scene';

export interface IPresenter {
    created_at: string;
    id: string;
    name: string;
    poster: string;
    thumbnail: string;
    url: string;
    order: number;
    isDefault: boolean;
    type: VideoType;
    presenterType?: ImageType;
    faces?: FaceSquare[];
    owner_id?: string;
    gender?: 'male' | 'female' | 'other';
    show?: boolean;
    v1Poster?: string;
    stitch?: boolean;
    is_streamable?: boolean;
    is_aiGenerated?: boolean;
    talkingPreviewUrl?: string;
    voice?: { type: Providers; voice_id: string };
    error?: JsonError;
    labels?: string[];
    faceRect?: FaceRect;
    deprecationDate?: string;
    background?: {
        source_url?: string;
        color?: string;
    };
}

export interface IClipPresenter extends IPresenter {
    train_id?: string;
    driverId?: string;
    videoUrl?: string;
    previewUrl?: string;
    is_streamable?: boolean;
    user_data?: string;
    status?: string;
    a2e_model?: A2eModel;
    is_greenscreen?: boolean;
}

export interface IAvatarTrain extends IPresenter {
    consent_id?: string;
    status?: string;
    progress?: {
        step: string;
        description?: string | null;
        details?: Record<string, any> | null;
    };
}

export interface IDreamsPresenter extends IPresenter {
    userPrompt?: string;
    prompt_id?: string;
    prompt?: string;
    seed?: string;
}

export interface IExpressPresenter extends IPresenter {
    created_by: string;
    source_url: string;
    consent_id: string;
    status: AvatarStatus;
    source_duration: number;
    previewUrl: string;
    voice_id?: string;
    is_green_screen: boolean;
}

export interface IExpressiveSentiment {
    id: string;
    avatar_id: string;
    owner_id: string;
    created_by: string;
    created_at: string;
    modified_at: string;
    sentiment: AvatarSentiment;
    description: string;
    preview_url?: string;
    inference_url?: string;
    voice?: {
        voice_id: string;
        type: Providers;
    };
}

export enum AvatarSentiment {
    Friendly = 'Friendly',
    Professional = 'Professional',
    Empathetic = 'Empathetic',
    Frustrated = 'Frustrated',
    Excited = 'Excited',
}

export interface IExpressivePresenter extends IPresenter {
    sentiments?: IExpressiveSentiment[];
}

export type Presenter = IClipPresenter &
    IDreamsPresenter &
    IPresenter &
    IExpressPresenter &
    IAvatarTrain &
    IExpressivePresenter;

export type INewPresenter = Omit<IPresenter, 'order' | 'thumbnail' | 'default'>;

export const presenterTypeMap: Record<VideoType, string> = {
    [VideoType.TALK]: 'Standard',
    [VideoType.CLIP]: 'Premium',
    [VideoType.Scene]: 'Express',
    [VideoType.TRAIN]: 'Train',
    [VideoType.EXPRESSIVE]: 'Expressive',
};

export function isPremiumPresenter(presenter: Presenter): boolean {
    return presenter.type === VideoType.CLIP;
}

export function isV2Presenter(id: string): boolean {
    return id.startsWith('v2_');
}

export function isDvpPresenter(presenter: Presenter): boolean {
    return isPremiumPresenter(presenter) && isV2Presenter(presenter.id);
}

export function isExpressPresenter(presenter: Presenter): presenter is IExpressPresenter {
    return presenter.type === VideoType.Scene;
}

export function isExpressivePresenter(presenter: Presenter): presenter is Presenter & IExpressivePresenter {
    return presenter.type === VideoType.EXPRESSIVE;
}

export function isDvpTrain(presenter: Presenter): boolean {
    return presenter.type === VideoType.TRAIN || presenter.id.startsWith('trn_');
}

export function isStreamablePresenter(presenter: Presenter): boolean {
    return !((isDvpPresenter(presenter) && !presenter.is_streamable) || isExpressPresenter(presenter));
}

export function isPublicPresenter(presenter: IPresenter): boolean {
    return presenter.owner_id == 'PUBLIC_D-ID';
}

export function getPresenterType(presenter?: Presenter) {
    if (!presenter) {
        return 'talk';
    }
    if (isExpressPresenter(presenter)) {
        return 'express';
    }
    if (isDvpPresenter(presenter)) {
        return 'premium_plus';
    }
    if (isPremiumPresenter(presenter)) {
        return 'premium';
    }

    return 'talk';
}

export function isDvpSelfServePresenter(presenter: Presenter): boolean {
    return (
        isDvpPresenter(presenter) &&
        !isPublicPresenter(presenter) &&
        JSON.parse(presenter.user_data || '{}').isSelfServe
    );
}
